// web/src/services/prismTracker.ts
// FRAME — PRISM behavioral signal collection
//
// Wraps PrismStore.recordEvent with convenience methods for each signal type.
// Tracks view duration, favorites, shares, disagreements, etc.
// Used by Home.tsx UI components to record user interactions with scored photos.

import type { PrismBehavioralEvent, PrismBehavioralEventType, PrismAestheticProperties } from '@frame/scoring-types';
import { BEHAVIORAL_SIGNAL_WEIGHTS } from '@frame/scoring-types';
import { classifyByBehavior, updatePreferenceVector } from '@frame/utils';
import { PrismStore } from './prismStore';
import { EngramService } from './engramService';

let activeSessionId = '';
let activeUserId = '';
let viewStartTime: Record<string, number> = {};
let pendingEngramEvents: Array<{ content: string; event_type: string; metadata: Record<string, unknown>; timestamp: number; node_type?: string }> = [];
const ENGRAM_SYNC_DEBOUNCE_MS = 5000; // Batch events every 5 seconds
let engramSyncTimer: ReturnType<typeof setTimeout> | null = null;
let engramInitialized = false;

/** Format a PRISM event as a natural language description for Engram memory. */
function formatEventForEngram(
  eventType: PrismBehavioralEventType,
  photoId: string,
  value?: number,
): string {
  const aesthetics = PrismStore.getAestheticsMap()[photoId];
  const parts: string[] = [];

  switch (eventType) {
    case 'favorited': parts.push('User favorited a photo'); break;
    case 'shared': parts.push('User shared a photo'); break;
    case 'deleted': parts.push('User deleted a photo from results'); break;
    case 'saved_edit': parts.push('User applied an edit suggestion'); break;
    case 'rejected_edit': parts.push('User rejected an edit suggestion'); break;
    case 'score_disagreed': parts.push('User disagreed with the score'); break;
    case 'viewed_duration': parts.push(`User spent ${value}s viewing photo details`); break;
    case 'viewed_detail': parts.push('User viewed photo diagnostic card'); break;
    case 'unfavorited': parts.push('User removed a photo from favorites'); break;
    case 'swipe_past': parts.push('User quickly swiped past a photo'); break;
  }

  if (aesthetics) {
    const traits: string[] = [];
    if (aesthetics.colorWarmth > 0.65) traits.push('warm tones');
    else if (aesthetics.colorWarmth < 0.35) traits.push('cool tones');
    if (aesthetics.contrastLevel > 0.7) traits.push('high contrast');
    else if (aesthetics.contrastLevel < 0.3) traits.push('soft contrast');
    if (aesthetics.colorSaturation > 0.7) traits.push('vibrant colors');
    else if (aesthetics.colorSaturation < 0.3) traits.push('muted colors');
    if (aesthetics.cropTightness > 0.5) traits.push('tight crop');
    if (aesthetics.smileIntensity > 0.6) traits.push('smiling expression');
    if (aesthetics.eyeContactScore > 0.7) traits.push('direct eye contact');
    if (aesthetics.isSelfie) traits.push('selfie');
    if (traits.length > 0) parts.push(`with ${traits.join(', ')}`);
    parts.push(`(composite: ${aesthetics.compositeScore.toFixed(1)}/10)`);
  }

  return parts.join(' ');
}

/** Flush pending events to Engram in a batch. */
async function flushEngramEvents(): Promise<void> {
  if (!EngramService.isEnabled() || !activeUserId || pendingEngramEvents.length === 0) return;
  const events = [...pendingEngramEvents];
  pendingEngramEvents = [];
  try {
    await EngramService.batchIngest(activeUserId, events);
    EngramService.setLastSyncTime(Date.now());
  } catch (err) {
    console.warn('[PRISM] Engram batch sync failed:', err);
    // Re-queue failed events (but don't let them accumulate forever)
    if (pendingEngramEvents.length < 200) {
      pendingEngramEvents.unshift(...events);
    }
  }
}

/** Schedule a debounced Engram sync. */
function scheduleEngramSync(): void {
  if (engramSyncTimer) clearTimeout(engramSyncTimer);
  engramSyncTimer = setTimeout(flushEngramEvents, ENGRAM_SYNC_DEBOUNCE_MS);
}

export const PrismTracker = {
  /** Set the current session ID (called when scoring starts). */
  setSession(sessionId: string, userId?: string): void {
    activeSessionId = sessionId;
    if (userId) activeUserId = userId;
    viewStartTime = {};

    // Initialize PRISM integration with Engram on first session (registers node types, prompt config, goals)
    if (EngramService.isEnabled() && !engramInitialized) {
      engramInitialized = true;
      EngramService.initializePrismIntegration().catch(() => {
        engramInitialized = false; // Retry next session
      });
    }
  },

  /** Record a behavioral event, and queue for Engram sync if enabled. */
  record(photoId: string, eventType: PrismBehavioralEventType, value?: number): void {
    if (!PrismStore.isEnabled()) return;
    const event: PrismBehavioralEvent = {
      photoId,
      sessionId: activeSessionId,
      timestamp: new Date().toISOString(),
      eventType,
      value,
    };
    PrismStore.recordEvent(event);

    // Queue for Engram sync
    if (EngramService.isEnabled() && activeUserId) {
      const content = formatEventForEngram(eventType, photoId, value);
      const aesthetics = PrismStore.getAestheticsMap()[photoId];
      pendingEngramEvents.push({
        content,
        event_type: `prism_${eventType}`,
        node_type: 'prism_behavioral',
        metadata: {
          photoId,
          signalWeight: BEHAVIORAL_SIGNAL_WEIGHTS[eventType],
          ...(aesthetics ? {
            compositeScore: aesthetics.compositeScore,
            warmth: aesthetics.colorWarmth,
            saturation: aesthetics.colorSaturation,
            contrast: aesthetics.contrastLevel,
            isOwner: aesthetics.isOwnerPresent,
          } : {}),
        },
        timestamp: Date.now(),
      });
      scheduleEngramSync();
    }
  },

  /** Track when user opens photo detail modal. */
  startViewDetail(photoId: string): void {
    viewStartTime[photoId] = Date.now();
    this.record(photoId, 'viewed_detail');
  },

  /** Track when user closes photo detail modal. Records duration if > 2s. */
  endViewDetail(photoId: string): void {
    const start = viewStartTime[photoId];
    if (start) {
      const durationSec = (Date.now() - start) / 1000;
      if (durationSec > 2) {
        this.record(photoId, 'viewed_duration', Math.round(durationSec));
      }
      delete viewStartTime[photoId];
    }
  },

  /** Track favorite toggle. */
  favorite(photoId: string): void {
    this.record(photoId, 'favorited');
  },

  /** Track unfavorite. */
  unfavorite(photoId: string): void {
    this.record(photoId, 'unfavorited');
  },

  /** Track share action. */
  share(photoId: string): void {
    this.record(photoId, 'shared');
  },

  /** Track score disagreement (thumbs down). */
  disagree(photoId: string): void {
    this.record(photoId, 'score_disagreed');
  },

  /**
   * Run preference learning after a session.
   * Classifies photos by behavioral signals and updates the preference vector.
   * Should be called when user finishes interacting with results.
   */
  learnFromSession(): void {
    if (!PrismStore.isEnabled() || PrismStore.isPaused()) return;

    const events = PrismStore.getEvents();
    const aestheticsMap = PrismStore.getAestheticsMap();
    const currentVector = PrismStore.getPreferenceVector();

    // Only learn if we have enough data
    if (events.length < 3) return;

    const { positive, negative } = classifyByBehavior(events, aestheticsMap);

    // Need at least 1 in each group for meaningful gradient
    if (positive.length === 0 && negative.length === 0) return;

    const updatedVector = updatePreferenceVector(currentVector, positive, negative);
    PrismStore.updatePreferenceVector(updatedVector);

    // Sync updated preference vector to Engram as an identity snapshot
    if (EngramService.isEnabled() && activeUserId) {
      const topPrefs: string[] = [];
      if (Math.abs(updatedVector.warmthPreference) > 0.3)
        topPrefs.push(updatedVector.warmthPreference > 0 ? 'prefers warm tones' : 'prefers cool tones');
      if (Math.abs(updatedVector.contrastPreference) > 0.3)
        topPrefs.push(updatedVector.contrastPreference > 0 ? 'likes high contrast' : 'likes soft contrast');
      if (Math.abs(updatedVector.saturationPreference) > 0.3)
        topPrefs.push(updatedVector.saturationPreference > 0 ? 'prefers vibrant colors' : 'prefers muted colors');
      if (Math.abs(updatedVector.smilePreference) > 0.3)
        topPrefs.push(updatedVector.smilePreference > 0 ? 'prefers smiling expressions' : 'prefers serious expressions');
      if (Math.abs(updatedVector.cropPreference) > 0.3)
        topPrefs.push(updatedVector.cropPreference > 0 ? 'prefers tight crops' : 'prefers wide shots');

      const summary = `PRISM preference update (confidence: ${updatedVector.confidence.toFixed(2)}, ${updatedVector.photosSampled} photos sampled). ` +
        (topPrefs.length > 0 ? `Style DNA: ${topPrefs.join(', ')}.` : 'Still learning preferences.');

      EngramService.ingest(activeUserId, summary, 'prism_preference_update', {
        preferenceVector: updatedVector,
        positive_count: positive.length,
        negative_count: negative.length,
      }, 'prism_preference_update');

      // Update "Style Discovery" goal progress with photos sampled count
      EngramService.updateGoalProgress('style_discovery', updatedVector.photosSampled, false).catch(() => {});

      // Track style milestones
      const milestones = [
        { threshold: 0.1, label: 'Getting to know you' },
        { threshold: 0.3, label: 'Tuning in' },
        { threshold: 0.5, label: 'Personalized' },
        { threshold: 0.8, label: 'Deeply personalized' },
      ];
      for (const m of milestones) {
        const prevConfidence = currentVector.confidence;
        if (prevConfidence < m.threshold && updatedVector.confidence >= m.threshold) {
          EngramService.ingest(activeUserId,
            `PRISM milestone reached: "${m.label}" (confidence: ${(updatedVector.confidence * 100).toFixed(0)}%, ${updatedVector.photosSampled} photos)`,
            'prism_milestone',
            { confidence: updatedVector.confidence, label: m.label, photosSampled: updatedVector.photosSampled },
            'prism_style_milestone',
          );
          break; // Only one milestone per update
        }
      }
    }
  },

  /** Flush any pending Engram events (call on session end or app unload). */
  flushEngram(): void {
    flushEngramEvents();
  },
};
